/**
 * cockpitChartData — PURE Aggregatoren für die M-F Cockpit-Charts
 * (`architecture-ux-density-v2.md` §5.9/§16).
 *
 * DISCIPLINE (Muster `loaders/cockpitLoaders.ts`): kein Firestore-Import,
 * kein React-Import, keine Seiteneffekte — nur Typ-Importe (zur Compile-Zeit
 * erased). Trivial unit-testbar ohne Mocking. Alle Geldbeträge Integer-Cents.
 * Alle Datumsvergleiche ISO-8601-String-Vergleiche bzw. UTC-Epochen-Diffs
 * (kein DST-Drift, Muster `utils/openItemStatus.ts`).
 *
 * Kanonische I/O-Paare (Testvertrag, §5.9):
 *   - `bucketMonthly('...', todayISO='2026-07-11', monthCount=6)` ⇒ exakt 6
 *     Buckets `2026-02 … 2026-07`, chronologisch aufsteigend; Einträge
 *     außerhalb des Fensters raus; malformed `dateISO`/`expenseDate` ignoriert
 *     (NIE NaN); `status:'draft'`-Expenses zählen nicht; Korrektur-Docs zählen
 *     mit gespeichertem Vorzeichen (Signed-Sum-Lehre — nicht filtern, Vorzeichen
 *     mitnehmen); leere Inputs ⇒ 6 Buckets mit 0/0.
 *   - `ageOpenItems`: `status==='paid'` raus; `dueDateISO=null` ⇒ `noDueDate`;
 *     `dueDateISO >= todayISO` ⇒ `notDue`; genau 30 Tage überfällig ⇒
 *     `due1to30` (Grenze inklusiv), 31 ⇒ `due31to60`, 61 ⇒ `due61plus`.
 *
 * @module utils/cockpitChartData
 */

import type { IncomeEntry } from './expenseSummary';
import type { Expense } from '../types/expenseTypes';

// ─── Shared date helpers (bewusst dupliziert, Muster `cockpitLoaders.ts` —
//     „Duplikat-Lader akzeptiert", keine Cross-Imports aus Loader/Page) ──────

/**
 * Validates an ISO date string strictly enough to avoid NaN/garbage
 * comparisons downstream. A malformed value is treated as "unknown" — the
 * caller MUST ignore the entry (never coerce to `0`/`NaN`).
 */
function isValidISODate(value: string | null | undefined): value is string {
  if (typeof value !== 'string') return false;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) return false;
  const month = Number(m[2]);
  const day = Number(m[3]);
  return month >= 1 && month <= 12 && day >= 1 && day <= 31;
}

/** UTC-midnight epoch (ms) for an ISO `YYYY-MM-DD` string — no DST drift. */
function utcEpochOf(dateISO: string): number {
  const y = Number(dateISO.slice(0, 4));
  const m = Number(dateISO.slice(5, 7));
  const d = Number(dateISO.slice(8, 10));
  return Date.UTC(y, m - 1, d);
}

/** Whole calendar days between two ISO dates (`to - from`), UTC-epoch-based. */
function daysBetweenUTC(fromISO: string, toISO: string): number {
  const MS_PER_DAY = 86_400_000;
  return Math.round((utcEpochOf(toISO) - utcEpochOf(fromISO)) / MS_PER_DAY);
}

// ─── Achsen-Formatierung (Ticks) ─────────────────────────────────────────────

/**
 * Kompakte Achsen-Währung für Chart-Ticks: Integer-Euro OHNE Cent, mit
 * Locale-Tausendergruppierung und nachgestelltem „ €". Bewusst gröber als
 * `formatCurrency` — Achsen-Ticks sollen lesbar bleiben und nicht am
 * Kartenrand clippen; volle Cent-Genauigkeit bleibt Tooltips/Tabellen
 * vorbehalten. `cents` ist der Integer-Cent-Rohwert (recharts-`dataKey`),
 * daher Division durch 100 vor der Formatierung.
 *
 * @param cents Integer-Cents (dataKey-Rohwert; NaN/Infinity ⇒ 0).
 * @param locale BCP-47-Locale (z. B. `i18n.language`).
 * @returns z. B. `'13.500 €'`, `'-4.500 €'`, `'0 €'` (de-DE).
 */
export function formatAxisCurrency(cents: number, locale: string): string {
  const euros = Math.round((Number.isFinite(cents) ? cents : 0) / 100);
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(euros)} €`;
}

// ─── #1 — Umsatz vs. Ausgaben, letzte N Monate (§16 Chart #1) ────────────────

/** Ein Monats-Bucket der Umsatz/Ausgaben-Grafik. Alle Beträge Integer-Cents. */
export interface MonthlyBucket {
  /** `'YYYY-MM'`. */
  yearMonth: string;
  incomeCents: number;
  expenseCents: number;
}

/**
 * Bucketed die letzten `monthCount` Kalendermonate (INKLUSIVE des Monats von
 * `todayISO`) chronologisch aufsteigend. `income`/`expenses` werden über
 * `dateISO`/`expenseDate` den jeweiligen Monats-Buckets zugeordnet — Einträge
 * außerhalb des Fensters fallen raus, malformed Daten werden ignoriert (nie
 * NaN-Poisoning). Nur `status==='booked'`-Ausgaben zählen; Korrektur-Docs
 * (negierte Koexistenz-Docs) zählen mit ihrem gespeicherten Vorzeichen (keine
 * Filterung nötig — Signed-Sum-Lehre, Muster `sumBookedExpensesMonth`).
 *
 * @param income - z. B. das Ergebnis von `loaders/cockpitLoaders#loadIncomeForUser`.
 * @param expenses - z. B. das Ergebnis von `api/expensesApi#getExpensesForUser`.
 * @param todayISO - Referenzdatum `YYYY-MM-DD` (i. d. R. heute), als Parameter
 *   übergeben, damit die Funktion pure/testbar bleibt.
 * @param monthCount - Anzahl der Monate im Fenster (Cockpit: 6).
 */
export function bucketMonthly(
  income: IncomeEntry[],
  expenses: Expense[],
  todayISO: string,
  monthCount: number,
): MonthlyBucket[] {
  if (!isValidISODate(todayISO) || !Number.isFinite(monthCount) || monthCount <= 0) return [];

  const year = Number(todayISO.slice(0, 4));
  const month = Number(todayISO.slice(5, 7));
  const currentMonthIndex = year * 12 + (month - 1);

  const buckets: MonthlyBucket[] = [];
  const bucketByYearMonth = new Map<string, MonthlyBucket>();
  for (let offset = monthCount - 1; offset >= 0; offset -= 1) {
    const monthIndex = currentMonthIndex - offset;
    const y = Math.floor(monthIndex / 12);
    const m = ((monthIndex % 12) + 12) % 12; // 0-based, defensive against negative modulo
    const yearMonth = `${y}-${String(m + 1).padStart(2, '0')}`;
    const bucket: MonthlyBucket = { yearMonth, incomeCents: 0, expenseCents: 0 };
    buckets.push(bucket);
    bucketByYearMonth.set(yearMonth, bucket);
  }

  for (const entry of income ?? []) {
    if (!entry || !isValidISODate(entry.dateISO)) continue;
    const bucket = bucketByYearMonth.get(entry.dateISO.slice(0, 7));
    if (bucket) bucket.incomeCents += entry.grossCents;
  }

  for (const expense of expenses ?? []) {
    if (!expense || expense.status !== 'booked') continue;
    if (!isValidISODate(expense.expenseDate)) continue;
    const bucket = bucketByYearMonth.get(expense.expenseDate.slice(0, 7));
    if (bucket) bucket.expenseCents += expense.grossCents;
  }

  return buckets;
}

// ─── #2 — Offene-Posten-Alterung (§16 Chart #2) ──────────────────────────────

export type AgingBucketId = 'notDue' | 'due1to30' | 'due31to60' | 'due61plus' | 'noDueDate';

/** Ein Alterungs-Bucket der OP-Alterungs-Grafik. `amountCents` Integer-Cents. */
export interface AgingBucket {
  id: AgingBucketId;
  amountCents: number;
  count: number;
}

/** Feste Bucket-Reihenfolge (normativ, §16) — `ageOpenItems` liefert IMMER alle 5. */
const AGING_BUCKET_ORDER: AgingBucketId[] = [
  'notDue',
  'due1to30',
  'due31to60',
  'due61plus',
  'noDueDate',
];

/**
 * Klassifiziert eine offene Zeile (bereits `paid`-gefiltert vom Aufrufer ODER
 * hier intern gefiltert) in genau einen der 5 Alterungs-Buckets.
 * Malformed `dueDateISO` (nicht `null`, aber kein valides ISO-Datum) wird
 * defensiv wie `null` behandelt (⇒ `noDueDate`) — nie NaN-Poisoning.
 */
function classifyAgingBucket(dueDateISO: string | null, todayISO: string): AgingBucketId {
  if (dueDateISO === null || !isValidISODate(dueDateISO)) return 'noDueDate';
  if (dueDateISO >= todayISO) return 'notDue';

  const daysOverdue = daysBetweenUTC(dueDateISO, todayISO);
  if (daysOverdue <= 30) return 'due1to30';
  if (daysOverdue <= 60) return 'due31to60';
  return 'due61plus';
}

/**
 * OP-Alterung über offene (nicht-`paid`) Zeilen — überfällige Tage =
 * `todayISO − dueDateISO` (Kalendertage, UTC-Mitternachts-Epochen, kein
 * DST-Drift). Liefert IMMER alle 5 Buckets in fester Reihenfolge (auch mit
 * `amountCents:0, count:0` wenn ein Bucket leer ist).
 *
 * @param rows - z. B. das Ergebnis von `loaders/cockpitLoaders#loadOpenItemRows`
 *   (nach `dueDateISO`-Ergänzung, §5.10).
 * @param todayISO - Referenzdatum `YYYY-MM-DD`.
 */
export function ageOpenItems(
  rows: Array<{
    status: 'open' | 'partially_paid' | 'paid' | 'overdue';
    openAmountCents: number;
    dueDateISO: string | null;
  }>,
  todayISO: string,
): AgingBucket[] {
  const totals = new Map<AgingBucketId, { amountCents: number; count: number }>(
    AGING_BUCKET_ORDER.map((id) => [id, { amountCents: 0, count: 0 }]),
  );

  if (isValidISODate(todayISO)) {
    for (const row of rows ?? []) {
      if (!row || row.status === 'paid') continue;
      const bucketId = classifyAgingBucket(row.dueDateISO, todayISO);
      const bucket = totals.get(bucketId)!;
      bucket.amountCents += row.openAmountCents;
      bucket.count += 1;
    }
  }

  return AGING_BUCKET_ORDER.map((id) => ({ id, ...totals.get(id)! }));
}
